import assert from 'minimalistic-assert';
import { SwapMode } from '../../common/types/index.js';
import { deconstructPairId } from '../../utils.js';
import { DEFAULT_OPTIONS, DEFAULT_SLIPPAGE, STRATEGY } from './constants.js';
import { parseFetchivalErrorWithOrder } from '../utils.js';
import { CreateDexOrderError, ExchangeError, MissingFeeAssetError, MissingPairError, MissingToAssetError, ProviderQuoteError, } from '../../errors.js';
import { retry } from '@exodus/simple-retry';
import { fromJSON } from '../../transaction/factory.js';
import { LOGS } from '../../index.js';
class Indirect {
    swapStrategy = STRATEGY;
    sources = ['indirect'];
    #assetsService;
    #fetchival;
    #tracer;
    #appName;
    #appVersion;
    #baseUrl;
    #options;
    #transactionPrivacyNetworks;
    #assetAvailabilityService;
    constructor(params) {
        this.#assetsService = params.assetsService;
        this.#fetchival = params.fetchival;
        this.#tracer = params.tracer;
        this.#appName = params.appName;
        this.#appVersion = params.appVersion;
        this.#baseUrl = params.baseUrl;
        this.#options = { ...DEFAULT_OPTIONS, ...params.options };
        this.#transactionPrivacyNetworks = params.transactionPrivacyNetworks || [];
        this.#assetAvailabilityService = params.assetAvailabilityService;
    }
    setTransactionPrivacyNetworks(transactionPrivacyNetworks) {
        this.#transactionPrivacyNetworks = transactionPrivacyNetworks;
    }
    async isPairSupported(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        const isCrossChain = fromAsset?.network !== toAsset?.network;
        if (isCrossChain && !this.#options.crossChainEnabled) {
            return false;
        }
        return this.#assetAvailabilityService.isPairSupportedBySource('indirect', pairId);
    }
    async getQuote(params) {
        const { amount, swapMode = SwapMode.ExactIn, pairId, slippage = DEFAULT_SLIPPAGE, fromAddress, toAddress, } = params;
        const { to } = deconstructPairId(pairId);
        const [trade, toAsset] = await Promise.all([
            this.#getTradeFromServer({
                fromAddress,
                toAddress,
                pairId,
                amount,
                slippage,
                swapMode,
            }),
            this.#assetsService.getOneById(to),
        ]);
        if (!toAsset) {
            throw new MissingToAssetError({ toAsset: to });
        }
        const rate = swapMode === SwapMode.ExactIn
            ? trade.amount.div(amount.toDefaultString()).toDefaultString()
            : amount.div(trade.amount.toDefaultString()).toDefaultString();
        const isExactIn = swapMode === SwapMode.ExactIn;
        return {
            fromAmount: isExactIn ? amount : trade.amount,
            toAmount: isExactIn ? trade.amount : amount,
            swapStrategy: this.swapStrategy,
            rateId: trade.id,
            rate,
            exchangeFeeData: {
                feeOpts: {
                    fee: trade.fee.amount,
                    gasLimit: trade.fee.gasLimit,
                },
            },
            exchangeTxData: trade.evmTransactions
                ? {
                    evmTransactions: trade.evmTransactions,
                }
                : {
                    solanaTransactions: trade.solanaTransactions,
                },
            expiry: trade.expiry,
            features: trade.features,
            kind: trade.kind,
        };
    }
    async exchange(params) {
        let orderId;
        try {
            const { pairId, fromAmount, fromAddress, swapMode = SwapMode.ExactIn, toAddress, simulateTx, quote, slippage = DEFAULT_SLIPPAGE, } = params;
            const trade = await this.#createTradeOrder({
                tradeId: quote.rateId,
                pairId,
                fromAddress,
                toAddress,
                fromAmount,
                swapMode,
                quote,
            });
            orderId = trade.orderId;
            const { fromAsset, toAsset } = await this.#getAssets(pairId);
            const transactions = await this.#getTransactionsFromQuote(quote);
            await this.#validateTransactions({
                transactions,
                fromAsset,
                toAsset,
                fromAddress,
                swapMode,
                slippage,
                fromAmount,
                toAddress,
                quote,
                simulateTx,
            });
            if (quote.features.includes('server-broadcast')) {
                return await this.#executeServerBroadcast({ params, fromAsset, trade });
            }
            const executedTransactions = await this.#executeTransactions({
                transactions,
                fromAsset,
                fromAmount,
                fromAddress,
                orderId: trade.orderId,
                createTxBundle: params.createTxBundle,
                sendTxBundle: params.sendTxBundle,
                createAndSendTx: params.createAndSendTx,
                signAndSendTx: params.signAndSendTx,
                createAndSendEvmTx: params.createAndSendEvmTx,
                toAddress,
            });
            return await this.#finalizeOrder({ executedTransactions, params, trade });
        }
        catch (err) {
            if (!err.orderId && orderId) {
                err.orderId = orderId;
            }
            await this.#updateTrade(params.quote.rateId, { error: err.message }).catch((err) => {
                console.warn(`Fail to update trade ${params.quote.rateId} with error ${err.message}`, err);
            });
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message, orderId }, { cause: err });
        }
    }
    async #executeServerBroadcast({ params, fromAsset, trade, }) {
        const { quote } = params;
        assert(params.signTx, 'signTx callback is required when server-broadcast feature is enabled');
        if (!('solanaTransactions' in quote.exchangeTxData)) {
            throw new ExchangeError({
                message: 'server-broadcast feature is only supported for Solana transactions',
            });
        }
        const swapTx = quote.exchangeTxData.solanaTransactions.find((tx) => tx.type === 'swap');
        const rawTx = swapTx?.data;
        assert(rawTx, 'Expected Solana transaction data for execute');
        const { signedTx } = await this.#withTimeTracker('SignTx', () => params.signTx({ assetId: fromAsset.id, rawTx }));
        const executeResponse = await this.#withTimeTracker('ExecuteTrade', () => this.#executeTradeOnServer(quote.rateId, signedTx));
        const providerOrder = await this.#withTimeTracker('GetProviderOrder', () => this.#request(new URL(`${this.#baseUrl}/orders/${encodeURIComponent(trade.orderId)}/provider-order`)).get());
        return {
            transactions: [{ id: executeResponse.transactionId, type: 'swapTransaction' }],
            svc: providerOrder.provider.slug,
            orderId: trade.orderId,
            providerOrderId: providerOrder.id,
        };
    }
    async #finalizeOrder({ executedTransactions, params, trade, }) {
        const swapTx = executedTransactions.find((e) => e.type === 'swapTransaction');
        assert(swapTx, 'expected swapTx');
        await this.#withTimeTracker('UpdateOrder', async () => this.#updateTrade(params.quote.rateId, {
            transactionId: swapTx.id,
        }));
        const providerOrder = await this.#withTimeTracker('GetProviderOrder', async () => this.#request(new URL(`${this.#baseUrl}/orders/${encodeURIComponent(trade.orderId)}/provider-order`)).get());
        return {
            transactions: executedTransactions,
            svc: providerOrder.provider.slug,
            orderId: trade.orderId,
            providerOrderId: providerOrder.id,
        };
    }
    async #createTradeOrder(params) {
        const { tradeId, pairId, fromAddress, toAddress, fromAmount, swapMode, quote } = params;
        this.#traceMessage(LOGS.CREATING_ORDER);
        return this.#withTimeTracker('CreateOrder', async () => {
            try {
                return await this.#request(new URL(`${this.#baseUrl}/trades/${encodeURIComponent(tradeId)}/order`)).post({});
            }
            catch (err) {
                const { message, orderId } = await parseFetchivalErrorWithOrder(err);
                throw new CreateDexOrderError({
                    message,
                    orderId,
                    details: {
                        pairId,
                        fromAddress,
                        toAddress,
                        amount: fromAmount,
                        swapMode,
                        quote,
                    },
                }, { cause: err });
            }
        });
    }
    async #getAssets(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId, fromAsset, toAsset });
        }
        return { fromAsset, toAsset };
    }
    async #getTransactionsFromQuote(quote) {
        this.#traceMessage(LOGS.DECODING_TX);
        const evmTxs = ('evmTransactions' in quote.exchangeTxData
            ? quote.exchangeTxData.evmTransactions.map((tx) => fromJSON({
                ...tx,
                type: 'evm',
                action: tx.type,
                kind: quote.kind,
            }))
            : []);
        const transactions = 'solanaTransactions' in quote.exchangeTxData
            ? quote.exchangeTxData.solanaTransactions.map((tx) => fromJSON({ ...tx, type: 'solana', action: tx.type, kind: quote.kind }))
            : evmTxs;
        if (transactions.length === 0) {
            throw new ExchangeError({ message: 'Invalid exchange tx data' });
        }
        return transactions;
    }
    async #validateTransactions(params) {
        const { transactions, fromAsset, toAsset, fromAddress, swapMode, slippage = DEFAULT_SLIPPAGE, fromAmount, toAddress, quote, simulateTx, } = params;
        this.#traceMessage(LOGS.CHECKING_ORDER_VALIDITY);
        await this.#withTimeTracker('CheckOrderIsValid', async () => {
            try {
                await Promise.all(transactions.map((tx) => tx.validate({
                    fromAsset,
                    toAsset,
                    fromAddress,
                    swapMode,
                    slippage,
                    fromAmount,
                    toAmount: quote.toAmount,
                    toAddress,
                    simulateTx,
                    requiredPreviewTxs: transactions.filter((tx) => tx.isEvm() && tx.action !== 'swap'),
                })));
            }
            catch (err) {
                throw new ExchangeError({ message: err.message }, { cause: err });
            }
        });
    }
    async #executeTransactions(params) {
        const { transactions, fromAsset, fromAmount, orderId } = params;
        this.#traceMessage(LOGS.SENDING_TX);
        const revokeTx = transactions.find((tx) => tx.action === 'revoke');
        const approveTx = transactions.find((tx) => tx.action === 'approve');
        const swapTx = transactions.find((tx) => tx.action === 'swap');
        if (!swapTx) {
            throw new ExchangeError({ message: 'Invalid exchange tx data' });
        }
        if (this.#transactionPrivacyNetworks.includes(fromAsset.network) &&
            typeof params.createTxBundle === 'function' &&
            typeof params.sendTxBundle === 'function') {
            const txBundle = await this.#withTimeTracker('CreateTxBundle', async () => params.createTxBundle({
                assetId: fromAsset.id,
                orderId,
                transactions: [revokeTx, approveTx, swapTx].flatMap((e) => (e ? [e] : [])),
            }));
            if (txBundle) {
                return this.#withTimeTracker('SendTxBundle', async () => params.sendTxBundle({
                    assetId: fromAsset.id,
                    txBundle,
                }));
            }
        }
        const executedTransactions = [];
        if (revokeTx) {
            const revokeTxId = await this.#withTimeTracker('RevokeTokens', async () => revokeTx.allowance({
                fromAsset,
                createAndSendEvmTx: params.createAndSendEvmTx,
                toAddress: params.toAddress,
            }));
            executedTransactions.push({ id: revokeTxId, type: 'preSetupTransaction' });
        }
        if (approveTx) {
            const approveTxId = await this.#withTimeTracker('ApproveTokens', async () => approveTx.allowance({
                fromAsset,
                createAndSendEvmTx: params.createAndSendEvmTx,
                toAddress: params.toAddress,
            }));
            executedTransactions.push({ id: approveTxId, type: 'setupTransaction' });
        }
        const swapTxId = await this.#withTimeTracker('SendTx', async () => swapTx.execute({
            fromAsset,
            createAndSendTx: params.createAndSendTx,
            signAndSendTx: params.signAndSendTx,
            createAndSendEvmTx: params.createAndSendEvmTx,
            amount: fromAmount,
            fromAddress: params.fromAddress,
            toAddress: params.toAddress,
        }));
        executedTransactions.push({ id: swapTxId, type: 'swapTransaction' });
        return executedTransactions;
    }
    async getLimits(params) {
        const { from, to } = deconstructPairId(params.pairId);
        const url = new URL(`${this.#baseUrl}/pairs/${encodeURIComponent(params.pairId)}/limits`);
        const [limits, fromAsset, toAsset] = await Promise.all([
            this.#request(url).get(),
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId: params.pairId, fromAsset, toAsset });
        }
        return [
            {
                [SwapMode.ExactIn]: {
                    min: fromAsset.currency.defaultUnit(limits.ExactIn.min.value),
                    max: fromAsset.currency.defaultUnit(limits.ExactIn.max.value),
                },
                [SwapMode.ExactOut]: {
                    min: toAsset.currency.defaultUnit(limits.ExactOut.min.value),
                    max: toAsset.currency.defaultUnit(limits.ExactOut.max.value),
                },
            },
        ];
    }
    setGeolocation(_geolocation) { }
    async #getTradeFromServer(params) {
        const { from, to } = deconstructPairId(params.pairId);
        const tradesUrl = new URL(`${this.#baseUrl}/trades`);
        tradesUrl.searchParams.set('extraFeatures', 'server-broadcast');
        const [fromAsset, toAsset, trade] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
            this.#request(tradesUrl)
                .post({
                pairId: params.pairId,
                fromAddress: params.fromAddress,
                toAddress: params.toAddress,
                ...(params.swapMode === SwapMode.ExactIn
                    ? { fromAmount: params.amount.toDefaultString() }
                    : { toAmount: params.amount.toDefaultString() }),
                slippage: params.slippage,
                swapMode: params.swapMode,
            })
                .catch((err) => {
                throw new ProviderQuoteError({
                    message: `Unable to get quote from the server for ${params.amount.toDefaultString()} ${from} -> ${to} (swapMode: ${params.swapMode}): ${err.message}`,
                }, { cause: err });
            }),
        ]);
        if (!fromAsset?.decimals || !toAsset?.decimals) {
            throw new ProviderQuoteError({
                message: `Unable to get quote from the server for${params.amount.toDefaultString()} ${from} -> ${to}`,
            });
        }
        const feeAsset = await this.#assetsService.getOneById(trade.fee.amount.assetId);
        if (!feeAsset) {
            throw new MissingFeeAssetError({ feeAsset: trade.fee.amount.assetId });
        }
        let quotedAmount;
        if (params.swapMode === SwapMode.ExactOut) {
            quotedAmount = fromAsset.currency.defaultUnit(trade.fromAmount.value);
        }
        else {
            quotedAmount = toAsset.currency.defaultUnit(trade.toAmount.value);
        }
        if (trade.solanaTransactions) {
            return {
                amount: quotedAmount,
                id: trade.id,
                fee: {
                    amount: feeAsset.currency.defaultUnit(trade.fee.amount.value),
                },
                solanaTransactions: trade.solanaTransactions,
                expiry: trade.expiry,
                features: trade.features,
                kind: trade.kind,
            };
        }
        if (trade.evmTransactions) {
            return {
                amount: quotedAmount,
                id: trade.id,
                fee: {
                    amount: feeAsset.currency.defaultUnit(trade.fee.amount.value),
                    gasLimit: trade.evmTransactions.reduce((acc, tx) => acc + tx.gasLimit, 0),
                },
                evmTransactions: trade.evmTransactions.map((tx) => ({
                    ...tx,
                    gasPrice: feeAsset.currency.baseUnit(parseInt(tx.gasPrice)),
                })),
                expiry: trade.expiry,
                features: trade.features,
                kind: trade.kind,
            };
        }
        throw new ProviderQuoteError({
            message: `Unable to get quote from the server for${params.amount.toDefaultString()} ${from} -> ${to}`,
        });
    }
    async #executeTradeOnServer(tradeId, signedTransaction) {
        return this.#request(new URL(`${this.#baseUrl}/trades/${encodeURIComponent(tradeId)}/execute`)).post({ signedTransaction });
    }
    async #updateTrade(tradeId, params) {
        const doFetch = async () => this.#request(new URL(`${this.#baseUrl}/trades/${encodeURIComponent(tradeId)}`)).patch(params);
        return retry(doFetch, { delayTimesMs: ['500ms', '1s'] })();
    }
    #request(url, { anonymousId } = {}) {
        return this.#fetchival(url, {
            headers: {
                'App-Name': this.#appName,
                'App-Version': this.#appVersion,
                ...(anonymousId ? { anonymousId } : {}),
            },
        });
    }
    #traceMessage(message) {
        this.#tracer?.traceMessage(message);
    }
    async #withTimeTracker(eventName, fn) {
        try {
            this.#tracer?.traceTime(eventName);
            return await fn();
        }
        finally {
            this.#tracer?.traceTimeEnd(eventName);
        }
    }
}
export default Indirect;
